import React from 'react';

import type { ChartOptions } from 'chart.js';
import { Line } from 'react-chartjs-2';

import { Box, Typography } from '@mui/material';

import { useAppSelector } from 'hooks';

import { SensorManager } from 'models';

interface ListItemProps {
    field: SensorManager.SensorField;
    filters?: SensorManager.SensorRecordFilters;
    chartOptions?: ChartOptions<'line'>;
    height?: React.CSSProperties['height'];
}

const SensorPlot: React.FunctionComponent<ListItemProps> = ({
    field,
    chartOptions,
    height,
}) => {
    const { records } = useAppSelector((root) => root.sensors);

    const data =
        records.object?.[field.id]?.map((record) => ({
            y: record.value,
            x: record.time,
        })) ?? [];

    let max: number | undefined = undefined;
    if (field.type == 'temperature') {
        max = 70;
    } else if (field.type == 'humidity') {
        max = 100;
    }
    if (field.chart_max_value_range) {
        max = field.chart_max_value_range;
    }

    let min: number | undefined = undefined;
    if (field.type == 'temperature') {
        min = 0;
    } else if (field.type == 'humidity') {
        min = 0;
    }
    if (field.chart_min_value_range) {
        min = field.chart_min_value_range;
    }

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateRows: 'auto auto 1fr',
                width: '100%',
            }}
        >
            <Typography variant="h6">{field.name}</Typography>
            <Typography>{field.description}</Typography>
            <Box
                sx={{
                    height,
                }}
            >
                <Line
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        spanGaps: 600_000,
                        plugins: {
                            legend: {
                                display: false,
                            },
                        },
                        scales: {
                            x: {
                                type: 'time',
                                time: {
                                    unit: 'minute',
                                },
                                adapters: {
                                    date: {
                                        zone: Intl.DateTimeFormat().resolvedOptions()
                                            .timeZone,
                                    },
                                },
                            },
                            y: {
                                ticks: {
                                    callback: (value) => {
                                        return `${value}${field.units}`;
                                    },
                                },
                                min,
                                max,
                            },
                        },
                        ...chartOptions,
                    }}
                    data={{
                        datasets: [
                            {
                                data,
                                tension: 0.1,
                                label: `${field.name} (${field.units})`,
                            },
                        ],
                    }}
                />
            </Box>
        </Box>
    );
};

SensorPlot.displayName = 'SensorPlot';
export default SensorPlot;
